import React, { useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { INTERVIEWMANAGEMENT_URL } from '../../Services/courseService';
import ResumeEditor from './ResumeEditor';
import LoadingPopup from './LoadingPopup';
import { motion } from 'framer-motion';
 
const ResumeAnalyzer = ({ userId, email ,getRemainingAttemptsCheck}) => {
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [error, setError] = useState(null);
    const [resumeurl, setResumeUrl] = useState('');

    const handleFileChange = (e) => {

        setFile(e.target.files[0]);
        setAnalysisResult(null);
        setError(null);
    };

    const handleAnalyze = async () => {
        setIsLoading(true);
        setAnalysisResult('');

        try {
            const response = await axios.get(`${INTERVIEWMANAGEMENT_URL}/v1/resumeAnalysis/remainingAttempts/${userId}`);
            const remainingAttempts = response?.data?.remainingAttempts;

            if (remainingAttempts < 1) {
                getRemainingAttemptsCheck();
                setIsLoading(false);
                return;
            }

            if (!file) {
                alert('Please upload a resume (DOCX)');
                setIsLoading(false);
                return;
            }

            setError(null);

            const formData = new FormData();
            formData.append('userId', userId);
            formData.append('email', email);
            formData.append('file', file);

            const res = await axios.post(
                `${INTERVIEWMANAGEMENT_URL}/v1/resumeAnalysis/upload`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setResumeUrl(res?.resumeUrl);

            await axios.put(`${INTERVIEWMANAGEMENT_URL}/v1/resumeAnalysis/update`, {
                userId,
                email
            });

            const rawResponse = res?.data?.pythonResponse;
            const parsedResult = parsePythonResponse(rawResponse);
            setAnalysisResult(parsedResult);

        } catch (err) {
            console.error(err);
            setError('Something went wrong while analyzing the resume.');
        } finally {
            setIsLoading(false);
        }
    };


    const parsePythonResponse = (raw) => {
        const result = {};

        try {
            const detectedSectionsMatch = raw.match(/Detected Sections: ({[\s\S]*?})\nNormalized Titles:/);
            if (detectedSectionsMatch) {
                result.detectedSections = eval(`(${detectedSectionsMatch[1]})`);
            }

            const resumeScoreMatch = raw.match(/"resume_score"\s*:\s*(\d+)/);
            result.resume_score = resumeScoreMatch ? parseInt(resumeScoreMatch[1], 10) : null;

            const grammarMatch = raw.match(/"grammar_issues"\s*:\s*({[\s\S]*?}),\n\s*"formatting_issues"/);
            result.grammar_issues = grammarMatch ? eval(`(${grammarMatch[1]})`) : {};

            const formattingMatch = raw.match(/"formatting_issues"\s*:\s*(\[[\s\S]*?\]),/);
            result.formatting_issues = formattingMatch ? eval(`(${formattingMatch[1]})`) : [];

            const keywordMatch = raw.match(/"keyword_analysis"\s*:\s*({[\s\S]*?}),\n\s*"personal_info"/);
            result.keyword_analysis = keywordMatch ? eval(`(${keywordMatch[1]})`) : {};

            const strengthsMatch = raw.match(/"strengths"\s*:\s*(\[[\s\S]*?\]),/);
            const weaknessesMatch = raw.match(/"weaknesses"\s*:\s*(\[[\s\S]*?\])\n/);
            result.strengths = strengthsMatch ? eval(strengthsMatch[1]) : [];
            result.weaknesses = weaknessesMatch ? eval(weaknessesMatch[1]) : [];
        } catch (e) {
            console.error("Parsing failed:", e);
        }

        return result;
    };

    const handleDownloadDocx = () => {
        if (!analysisResult) return;

        const paragraphs = [
            new Paragraph({
                children: [new TextRun({ text: 'Resume Analysis Report', bold: true, size: 28 })],
                spacing: { after: 300 },
            }),
            new Paragraph(`Resume Score: ${analysisResult.resume_score}`),
            new Paragraph(''),

            new Paragraph({ text: 'Grammar Issues:', bold: true }),
            ...Object.entries(analysisResult.grammar_issues || {}).flatMap(([section, issues]) => [
                new Paragraph({ text: section, bold: true }),
                ...issues.map(issue =>
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: `${issue.message} — `,
                                bold: true,
                            }),
                            new TextRun({
                                text: issue.incorrect ? `Incorrect: ${issue.incorrect}. ` : '',
                                italics: true,
                                color: '#FF0000',
                            }),
                            // new TextRun({
                            //   text: issue.context ? `Context: ${issue.context} ` : '',
                            // }),
                            new TextRun({
                                text: `Suggestions: ${issue.suggestions?.join(', ') || 'None'}`,
                            }),
                        ]

                    })
                )
            ]),

            new Paragraph({ text: 'Formatting Issues:', bold: true }),
            ...(analysisResult.formatting_issues || []).map(issue => new Paragraph(issue)),

            new Paragraph({ text: 'Keyword Analysis:', bold: true }),
            new Paragraph(`Found Keywords: ${(analysisResult.keyword_analysis?.found_keywords || []).join(', ')}`),
            new Paragraph(`Missing Keywords: ${(analysisResult.keyword_analysis?.missing_keywords || []).join(', ')}`),

            new Paragraph({ text: 'Strengths:', bold: true }),
            ...(analysisResult.strengths || []).map(s => new Paragraph(s)),

            new Paragraph({ text: 'Weaknesses:', bold: true }),
            ...(analysisResult.weaknesses || []).map(w => new Paragraph(w)),
        ];

        const doc = new Document({ sections: [{ children: paragraphs }] });

        Packer.toBlob(doc).then((blob) => {
            saveAs(blob, 'resume_analysis_report.docx');
        });
    };

    const escapeRegExp = (string) =>
        string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    return (
        <div className='resume-analyzer-main-cointainer'>
            <div>
                <div style={{ maxWidth: '1200px', margin: '100px auto', padding: '2rem' }}>
                    <h2>Resume Analyzer</h2>
                    <p>Fine-tune your resume and land more interviews.</p>
                    <input type="file" accept=".docx" onChange={handleFileChange} />
                    <button className='re-button ai-button' onClick={handleAnalyze} disabled={isLoading} style={{ marginTop: '10px' }}>
                        {isLoading ? <LoadingPopup /> : 'Analyze Resume'}

                    </button>

                    {error && <p style={{ color: 'red' }}>{error}</p>}
                    {isLoading && <p>Processing... Please wait.</p>}
                    {analysisResult && (
                        <>
                            <ResumeEditor
                                file={file}
                                grammarIssues={Object.values(analysisResult.grammar_issues || {}).flat()}
                            />

                            {/* <ResumeEditor resumeUrl={resumeurl } /> */}
                        </>
                    )}
                    {analysisResult && (
                        <div style={{ marginTop: '2rem' }}>
                            <h3>Resume Analysis Report</h3>
                            <div className='resume-score-container'><strong>Resume Score:</strong>
                                <div className="animation-container">


                                    <h2>{analysisResult.resume_score}</h2>
                                    <motion.div
                                        className="loader-half loader-top"
                                        initial={{ rotate: -22 }}
                                        animate={{ rotate: 240 }}
                                        transition={{ duration: 5, ease: 'anticipate', repeat: Infinity, repeatType: 'reverse' }}
                                    ></motion.div>
                                </div> </div>

                            <h4>Strengths</h4>
                            <ul>
                                {(analysisResult.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
                            </ul>

                            <h4>Weaknesses</h4>
                            <ul>
                                {(analysisResult.weaknesses || []).map((w, i) => <li key={i}>{w}</li>)}
                            </ul>

                            <h4>Grammar Issues</h4>
                            {Object.entries(analysisResult.grammar_issues || {}).map(([section, issues]) => (
                                <div key={section}>
                                    <strong>{section}</strong>
                                    <ul>
                                        {issues.map((issue, idx) => {
                                            const { context = '', incorrect, suggestions, message } = issue;
                                            const highlightedContext = (() => {
                                                if (incorrect && context) {
                                                    return context.replace(
                                                        new RegExp(escapeRegExp(incorrect), 'gi'),
                                                        (match) => `<span style="color:red;font-weight:bold;">${match}</span>`
                                                    );
                                                } else if (context) {
                                                    return `<span>${context}</span>`;
                                                } else if (incorrect) {
                                                    return `<span style="color:red;">${incorrect}</span>`;
                                                } else {
                                                    return `<span style="color:red;">⚠️ Issue found but no context available</span>`;
                                                }
                                            })();

                                            return (
                                                <li key={idx}>
                                                    <div><strong>{message}</strong></div>
                                                    <div dangerouslySetInnerHTML={{ __html: highlightedContext }} />
                                                    <div><em>Suggestions:</em> {suggestions?.join(', ') || 'None'}</div>
                                                </li>
                                            );
                                        })}

                                    </ul>
                                </div>
                            ))}

                            <h4>Formatting Issues</h4>
                            <ul>
                                {(analysisResult.formatting_issues || []).map((issue, idx) => <li key={idx}>{issue}</li>)}
                            </ul>

                            <h4>Keyword Analysis</h4>
                            <p><strong>Found:</strong> {analysisResult.keyword_analysis?.found_keywords?.join(', ')}</p>
                            <p><strong>Missing:</strong> {analysisResult.keyword_analysis?.missing_keywords?.join(', ')}</p>


                            <button className='re-button ai-button' onClick={handleDownloadDocx} style={{ marginTop: '20px' }}>
                                Download Report
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: '2rem' }}>
                    <h3>How It Works</h3>
                    <ol style={{ lineHeight: '1.8' }}>
                        <li><strong>Upload:</strong> Add your resume in DOCX format.</li>
                        <li><strong>Analyze:</strong> We review key sections, grammar, formatting, and keywords.</li>
                        <li><strong>Improve:</strong> Get actionable feedback to optimize your resume for better job matches.</li>
                        <li><strong>Download:</strong> Save a detailed report
                            {/* or update your profile resume directly */}
                            .</li>
                    </ol>
                </div>

            </div>

        </div>
    );
};

export default ResumeAnalyzer;